/**
 * 需要想定 (週間) 画面。読み取り専用。
 *
 * URL: /demand/forecast-weekly?workflowId=<id>
 * 週間ケースの対象日から 14 日のサマリ表と、選択日の 48 コマ表・チャートを出す。
 * 日付の切り替えは取得済みの page の中で行い、再取得しない (AC-DFW-002)。
 * 公開状態の gate は route (`WeeklySectionGate`) が持ち、この page は flag を知らない。
 */
import type { ReactElement } from 'react'
import { useMemo, useState } from 'react'

import { Code } from '@connectrpc/connect'
import { useTranslation } from 'react-i18next'

import { Box, Grid, Stack } from '@enechain/ecloud-designsystem'

import { CompactAlert } from '~/components/common/CompactAlert'
import { PageLayout } from '~/components/common/PageLayout'
import { RegionLoading } from '~/components/common/RegionLoading'
import { WorkflowNotFoundView } from '~/components/common/WorkflowNotFoundView'
import { useCurrentWorkflowCase } from '~/hooks/workflowCase/useCurrentWorkflowCase'
import type { CivilDate } from '~/models/civilDate'
import { formatCivilDateSlash } from '~/models/civilDate'
import { ForecastChart } from '~/pages/DemandForecastDaily/components/ForecastChart'
import { InvalidWorkflowIdView } from '~/pages/DemandForecastDaily/components/InvalidWorkflowIdView'
import { KomaGrid } from '~/pages/DemandForecastDaily/components/KomaGrid'
import { WeeklySummaryTable } from '~/pages/DemandForecastWeekly/components/WeeklySummaryTable'
import { useGetDemandForecastWeekly } from '~/pages/DemandForecastWeekly/hooks/useGetDemandForecastWeekly'
import {
  buildSelectedDayView,
  parseWeeklyPage,
  selectDay,
  summaryColumnOf,
} from '~/pages/DemandForecastWeekly/models/weeklyPage'
import { routePaths } from '~/routes/routePaths'

const NO_HIDDEN_KEYS = new Set<string>()

export const DemandForecastWeeklyPage = (): ReactElement => {
  const { t } = useTranslation()
  const { caseId, wfCase, isWorkflowNotFound } = useCurrentWorkflowCase()
  const query = useGetDemandForecastWeekly({ caseId })
  const page = useMemo(
    () => (query.data === undefined ? undefined : parseWeeklyPage(query.data)),
    [query.data],
  )
  const [picked, setPicked] = useState<CivilDate | undefined>()
  const [isSummaryCollapsed, setSummaryCollapsed] = useState(false)
  const day = page === undefined ? undefined : selectDay(page, picked)
  const view = useMemo(
    () =>
      day === undefined || page === undefined
        ? undefined
        : buildSelectedDayView(day, page.gateCloseFrontier),
    [day, page],
  )

  const layout = (children: ReactElement): ReactElement => (
    <PageLayout
      breadcrumbItems={[
        {
          label: t('common:route.demandForecastList'),
          to: routePaths.demandForecastList.path,
        },
        // 単日 (DemandForecastDailyLayout) と同じ中間クラム: ワークフロー ID →
        // ケース詳細。見つからないケースの ID はパンくずに出さない。
        ...(caseId !== undefined && !isWorkflowNotFound
          ? [
              {
                label:
                  wfCase?.workflowId !== undefined && wfCase.workflowId !== ''
                    ? wfCase.workflowId
                    : caseId,
                to: routePaths.workflowCaseDetail.path.replace(
                  ':caseId',
                  caseId,
                ),
              },
            ]
          : []),
        { label: t('common:route.demandForecastWeekly') },
      ]}
    >
      {children}
    </PageLayout>
  )

  if (isWorkflowNotFound) {
    return layout(<WorkflowNotFoundView />)
  }
  // それ以外の取得失敗は hook の throwOnError で既存の server error 表示へ。
  if (page === undefined && query.error?.code === Code.InvalidArgument) {
    return layout(<InvalidWorkflowIdView />)
  }
  if (page === undefined || day === undefined || view === undefined) {
    return layout(<RegionLoading minHeight="200px" isLoading />)
  }
  return layout(
    <Stack gap="4">
      <WeeklySummaryTable
        columns={page.days.map(summaryColumnOf)}
        isCollapsed={isSummaryCollapsed}
        onCollapsedChange={setSummaryCollapsed}
        onSelect={setPicked}
        selected={day.date}
      />
      {page.hasBeyondHorizon && (
        <CompactAlert
          status="info"
          title={t('page:demandForecastWeekly.beyondHorizonNotice')}
        />
      )}
      <div data-testid="weekly-selected-day">
        {t('page:demandForecastWeekly.selectedDay', {
          date: formatCivilDateSlash(day.date),
        })}
      </div>
      {/* React mock と同じ「左に 48 コマ表・右にチャート」の 2 カラム (狭い幅では縦に積む)。
          行高を minmax(0, 1fr) で確定させ、48 行は表の中だけでスクロールさせる。 */}
      <Grid
        flex="1"
        flexShrink={0}
        gridGap={4}
        gridTemplateRows="minmax(0, 1fr)"
        gridTemplateColumns={{
          base: '1fr',
          md: 'minmax(0, max-content) minmax(0, 1fr)',
        }}
        minHeight="lg"
        data-testid="weekly-selected-day-grid"
      >
        <Box flex="1" overflow="hidden" minWidth="0" minHeight={0}>
          <KomaGrid view={view} />
        </Box>
        <Box overflow="hidden" minWidth="0" minHeight={0}>
          <ForecastChart hiddenKeys={NO_HIDDEN_KEYS} view={view} />
        </Box>
      </Grid>
    </Stack>,
  )
}
